/**
 * src/styles/airTheme.js
 *
 * One control kit for the client portal, the Job Work / Courier portals and
 * the Partner pages, modelled on Airbnb: white page, near-black ink, soft
 * grey lines, rounded controls, gold kept as the Marqland accent.
 *
 *  • AIR            – colour, type, radius and shadow tokens
 *  • AIR_CSS        – global rules, scoped to an `.air` root. Also restyles the
 *                     legacy classes from marqlandTheme.js (.fi, .lbl, .btn-gold,
 *                     .btn-outline, .pill, .nav …) inside that root, so the
 *                     marketing HomePage keeps its own navy look.
 *  • AIR_STYLE      – inline style objects for the search pill and buttons
 *  • <AirSelect>    – dropdown (in a search pill, or boxed on its own)
 *  • <AirSwitch>    – on/off toggle
 *
 * Usage:
 *   <div className="air"> <style>{AIR_CSS}</style> … </div>
 */
import React from 'react';
import { createPortal } from 'react-dom';

export const AIR = {
  ink: '#222222', sub: '#484848', muted: '#6a6a6a', faint: '#929292',
  line: '#ebebeb', line2: '#dddddd', field: '#b0b0b0', studio: '#f7f7f7', white: '#ffffff',
  gold: '#b8975a', goldText: '#7d6233', goldGrad: 'linear-gradient(135deg, #c9a663, #b8975a)',
  danger: '#c13515', dangerBg: '#fff8f6', success: '#008a05', successBg: '#f2fbf2',
  serif: "'Cormorant Garamond',Georgia,serif", sans: "'Jost',sans-serif",
  rControl: 8, rCard: 12, rPill: 40,
  shadowBar: '0 3px 12px rgba(0,0,0,0.08)',
  shadowCard: '0 6px 16px rgba(0,0,0,0.12)',
  shadowPop: '0 6px 20px rgba(0,0,0,0.2)',
};

const chevronSvg = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23222222' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")";

export const AIR_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400&family=Jost:wght@300;400;500;600&display=swap');
  .air { --navy: #ffffff; --navy-mid: #f7f7f7; background: ${AIR.white}; color: ${AIR.ink}; font-family: ${AIR.sans}; -webkit-font-smoothing: antialiased; }
  .air *, .air *::before, .air *::after { box-sizing: border-box; }
  .air .sf { font-family: ${AIR.serif}; }

  /* ── Fields: text, number, date, select, textarea ── */
  .air .fi,
  .air-forms input[type=text], .air-forms input[type=email], .air-forms input[type=tel], .air-forms input[type=password],
  .air-forms input[type=number], .air-forms input[type=search], .air-forms input[type=date], .air-forms input[type=url],
  .air-forms select, .air-forms textarea, .air select.air-field, .air input.air-field, .air textarea.air-field {
    width: 100%; min-height: 48px; padding: 12px 14px; background: ${AIR.white};
    border: 1px solid ${AIR.field}; border-radius: ${AIR.rControl}px;
    font-family: ${AIR.sans}; font-size: 15px; font-weight: 400; letter-spacing: normal; color: ${AIR.ink};
    outline: none; transition: border-color .15s, box-shadow .15s;
  }
  .air-forms textarea, .air textarea.fi { min-height: 96px; line-height: 1.5; }
  .air .fi::placeholder, .air input::placeholder, .air textarea::placeholder { color: ${AIR.faint}; }
  .air .fi:focus, .air-forms input:focus, .air-forms select:focus, .air-forms textarea:focus, .air .air-field:focus { border-color: ${AIR.ink}; box-shadow: 0 0 0 1px ${AIR.ink}; }
  .air .fi:disabled, .air-forms input:disabled, .air-forms select:disabled { background: ${AIR.studio}; color: ${AIR.faint}; cursor: not-allowed; }
  .air-forms select, .air select.air-field { -webkit-appearance: none; appearance: none; padding-right: 40px; cursor: pointer;
    background-image: ${chevronSvg}; background-repeat: no-repeat; background-position: right 14px center; }
  .air input[type=date] { cursor: pointer; }
  .air input[type=date]::-webkit-calendar-picker-indicator { cursor: pointer; opacity: .7; }
  .air input[type=checkbox], .air input[type=radio] { accent-color: ${AIR.ink}; width: 18px; height: 18px; cursor: pointer; }
  .air .lbl { display: block; font-family: ${AIR.sans}; font-size: 13px; font-weight: 600; letter-spacing: normal; text-transform: none; color: ${AIR.ink}; margin-bottom: 6px; }

  /* ── Buttons ── */
  .air .btn-gold, .air .air-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 48px; padding: 0 24px;
    background: ${AIR.ink}; color: ${AIR.white}; border: none; border-radius: ${AIR.rControl}px; cursor: pointer;
    font-family: ${AIR.sans}; font-size: 15px; font-weight: 600; letter-spacing: normal; text-transform: none; text-decoration: none;
    transition: background .15s, transform .1s;
  }
  .air .btn-gold:hover, .air .air-btn:hover { background: #000; transform: none; }
  .air .btn-gold:active, .air .air-btn:active { transform: scale(0.98); }
  .air .btn-gold:disabled, .air .air-btn:disabled { background: ${AIR.line2}; color: ${AIR.white}; cursor: not-allowed; }
  .air .btn-outline, .air .air-btn-ghost {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 48px; padding: 0 24px;
    background: ${AIR.white}; color: ${AIR.ink}; border: 1px solid ${AIR.ink}; border-radius: ${AIR.rControl}px; cursor: pointer;
    font-family: ${AIR.sans}; font-size: 15px; font-weight: 600; letter-spacing: normal; text-transform: none; text-decoration: none;
  }
  .air .btn-outline:hover, .air .air-btn-ghost:hover { background: ${AIR.studio}; border-color: ${AIR.ink}; }
  .air .air-btn-brand { background: ${AIR.goldGrad}; }
  .air .air-btn-brand:hover { background: ${AIR.gold}; }
  .air .air-link { background: none; border: none; padding: 0; cursor: pointer; font-family: ${AIR.sans}; font-size: 14px; font-weight: 600; color: ${AIR.ink}; text-decoration: underline; text-underline-offset: 3px; }
  .air .air-icon-btn { width: 40px; height: 40px; border-radius: 50%; border: 1px solid ${AIR.line2}; background: ${AIR.white}; color: ${AIR.ink}; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
  .air .air-icon-btn:hover { box-shadow: 0 2px 6px rgba(0,0,0,0.12); }

  /* ── Pills, chips, labels ── */
  .air .pill { display: inline-flex; align-items: center; gap: 8px; padding: 6px 14px; border: none; border-radius: ${AIR.rPill}px;
    background: ${AIR.studio}; color: ${AIR.ink}; font-family: ${AIR.sans}; font-size: 13px; font-weight: 500; letter-spacing: normal; text-transform: none; }
  .air .air-chip { display: inline-flex; align-items: center; gap: 8px; min-height: 40px; padding: 0 18px; border-radius: ${AIR.rPill}px;
    border: 1px solid ${AIR.line2}; background: ${AIR.white}; color: ${AIR.ink}; font-family: ${AIR.sans}; font-size: 14px; font-weight: 500; cursor: pointer; white-space: nowrap; }
  .air .air-chip:hover { border-color: ${AIR.ink}; }
  .air .air-chip[aria-pressed=true], .air .air-chip.on { background: ${AIR.ink}; border-color: ${AIR.ink}; color: ${AIR.white}; }
  .air .air-badge { display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: ${AIR.rPill}px; font-size: 12px; font-weight: 600; background: ${AIR.studio}; color: ${AIR.ink}; }

  /* ── Tabs: underline style ── */
  .air .air-tabs { display: flex; gap: 28px; overflow-x: auto; scrollbar-width: none; border-bottom: 1px solid ${AIR.line}; }
  .air .air-tab { flex: none; padding: 14px 0 12px; margin-bottom: -1px; background: none; border: none; border-bottom: 2px solid transparent;
    font-family: ${AIR.sans}; font-size: 14px; font-weight: 500; letter-spacing: normal; text-transform: none; color: ${AIR.muted}; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; }
  .air .air-tab:hover { color: ${AIR.ink}; }
  .air .air-tab[aria-selected=true], .air .air-tab.on { color: ${AIR.ink}; font-weight: 600; border-bottom-color: ${AIR.ink}; }

  /* ── Segmented toggle (e.g. Night stay | Day outing) ── */
  .air [role=tablist].air-seg-toggle { display: inline-flex; gap: 2px; padding: 4px; background: ${AIR.studio}; border-radius: ${AIR.rPill}px; border: none; }

  /* ── Cards & modals ── */
  .air .air-card { background: ${AIR.white}; border: 1px solid ${AIR.line2}; border-radius: ${AIR.rCard}px; }
  .air .air-card-raised { background: ${AIR.white}; border: 1px solid ${AIR.line2}; border-radius: ${AIR.rCard}px; box-shadow: ${AIR.shadowCard}; }
  .air .air-overlay { position: fixed; inset: 0; z-index: 300; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; padding: 16px; }
  .air .air-modal { background: ${AIR.white}; border-radius: ${AIR.rCard}px; box-shadow: ${AIR.shadowPop}; width: 100%; position: relative; max-height: calc(100vh - 32px); overflow-y: auto; }

  /* ── Search pill segments + menus (used by AirSelect) ── */
  .air .air-seg:not(:disabled):hover { background: ${AIR.line} !important; }
  .air .air-opt:hover { background: ${AIR.studio} !important; }
  .air .air-in:focus-within { background: ${AIR.white}; box-shadow: 0 6px 20px rgba(0,0,0,0.12); }
  .air .air-in:hover:not(:focus-within) { background: ${AIR.line}; }
  .air .air-in input, .air .air-seg input { border: none !important; box-shadow: none !important; min-height: 0; padding: 0; background: transparent; border-radius: 0; }

  /* ── Switch ── */
  .air .air-switch { position: relative; flex: none; width: 48px; height: 32px; border-radius: 32px; border: none; padding: 0; cursor: pointer; background: ${AIR.field}; transition: background .2s; }
  .air .air-switch[aria-checked=true] { background: ${AIR.ink}; }
  .air .air-switch::after { content: ''; position: absolute; top: 2px; left: 2px; width: 28px; height: 28px; border-radius: 50%; background: ${AIR.white}; box-shadow: 0 1px 3px rgba(0,0,0,0.3); transition: transform .2s; }
  .air .air-switch[aria-checked=true]::after { transform: translateX(16px); }
  .air .air-switch:disabled { opacity: .4; cursor: not-allowed; }

  /* Checkbox drawn as a switch: <input type="checkbox" className="air-toggle" /> */
  .air input[type=checkbox].air-toggle { -webkit-appearance: none; appearance: none; position: relative; flex: none; width: 40px; height: 24px; margin: 0;
    border-radius: 24px; background: ${AIR.field}; cursor: pointer; transition: background .2s; }
  .air input[type=checkbox].air-toggle::before { content: ''; position: absolute; top: 2px; left: 2px; width: 20px; height: 20px; border-radius: 50%;
    background: ${AIR.white}; box-shadow: 0 1px 3px rgba(0,0,0,0.3); transition: transform .2s; }
  .air input[type=checkbox].air-toggle:checked { background: ${AIR.ink}; }
  .air input[type=checkbox].air-toggle:checked::before { transform: translateX(16px); }

  /* ── Price range (Airbnb Minimum / Maximum): two separate rounded boxes, no spinner arrows ── */
  .air .air-range { display: flex; align-items: flex-end; gap: 12px; min-width: 0; }
  .air .air-range-col { display: flex; flex-direction: column; gap: 8px; flex: 1 1 0; min-width: 0; }
  .air .air-range-label { font-family: ${AIR.sans}; font-size: 12px; font-weight: 500; color: ${AIR.muted}; }
  .air .air-range-dash { flex: none; width: 12px; height: 1px; background: ${AIR.field}; align-self: center; }
  .air .air-range.has-labels .air-range-dash { align-self: flex-end; margin-bottom: 28px; }
  .air .air-money { display: flex; align-items: center; gap: 2px; height: 56px; padding: 0 20px; background: ${AIR.white};
    border: 1px solid ${AIR.field}; border-radius: ${AIR.rPill}px; cursor: text; transition: border-color .15s, box-shadow .15s; min-width: 0; }
  .air .air-money.sm { height: 40px; padding: 0 14px; }
  .air .air-money:hover { border-color: ${AIR.ink}; }
  .air .air-money:focus-within { border-color: ${AIR.ink}; box-shadow: 0 0 0 1px ${AIR.ink}; }
  .air .air-money.invalid { border-color: ${AIR.danger}; }
  .air .air-money-cur { font-family: ${AIR.sans}; font-size: 16px; color: ${AIR.ink}; flex: none; }
  .air .air-money.sm .air-money-cur { font-size: 14px; }
  .air .air-money input { flex: 1 1 auto; width: 100%; min-width: 0; height: auto; min-height: 0 !important; padding: 0 !important; margin: 0;
    border: none !important; border-radius: 0 !important; box-shadow: none !important; outline: none; background: transparent !important;
    font-family: ${AIR.sans} !important; font-size: 16px !important; color: ${AIR.ink}; }
  .air .air-money.sm input { font-size: 14px !important; }
  .air .air-money input::placeholder { color: ${AIR.faint}; }
  .air input[type=number]::-webkit-inner-spin-button, .air input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
  .air input[type=number] { -moz-appearance: textfield; appearance: textfield; }

  /* ── Stepper (− 1 +) ── */
  .air .air-step { width: 32px; height: 32px; border-radius: 50%; border: 1px solid ${AIR.field}; background: ${AIR.white}; color: ${AIR.ink}; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; font-size: 16px; line-height: 1; }
  .air .air-step:hover { border-color: ${AIR.ink}; }
  .air .air-step:disabled { opacity: .3; cursor: not-allowed; }

  /* ── Legacy marqlandTheme classes, light inside .air ── */
  .air .nav { background: ${AIR.white} !important; border-bottom: 1px solid ${AIR.line} !important; backdrop-filter: none; -webkit-backdrop-filter: none; height: 72px; }
  .air .sub-btn { padding: 8px 16px; border-radius: 40px; font-family: ${AIR.sans}; font-size: 14px; font-weight: 500; letter-spacing: 0; text-transform: none; border: 1px solid ${AIR.line2}; min-height: 36px; }
  .air .sub-on { background: ${AIR.ink}; color: ${AIR.white}; border-color: ${AIR.ink}; }
  .air .sub-off { background: ${AIR.white}; color: ${AIR.ink}; border-color: ${AIR.line2}; }
  .air .sub-off:hover { border-color: ${AIR.ink}; color: ${AIR.ink}; }
  .air .toast-card { background: ${AIR.white}; border: none; border-radius: 12px; box-shadow: 0 6px 20px rgba(0,0,0,.2); color: ${AIR.ink}; }
  .air .nav-logo { color: ${AIR.ink}; font-size: 24px; font-weight: 500; letter-spacing: 0.01em; }
  .air .nav .btn-gold { min-height: 40px; padding: 0 18px !important; font-size: 14px; }
  .air .mq-navbar-toggle { border: 1px solid ${AIR.line2} !important; border-radius: 50%; color: ${AIR.ink} !important; width: 40px !important; height: 40px !important; }
  .air .mq-navbar-mobile-panel { background: ${AIR.white} !important; border-bottom: 1px solid ${AIR.line} !important; }
  .air .lbl + .fi, .air .lbl + input, .air .lbl + select, .air .lbl + textarea, .air .lbl + div { margin-top: 0; }
  .air .divider { background: ${AIR.line2}; }

  @media (max-width: 640px) {
    .air .btn-gold, .air .btn-outline, .air .air-btn, .air .air-btn-ghost { min-height: 44px; padding: 0 18px; font-size: 14px; }
    .air .nav { height: 64px; padding: 0 16px !important; }
    .air .nav-logo { font-size: 20px; }
  }
`;

export const AIR_STYLE = {
  bar: { display: 'flex', alignItems: 'stretch', background: AIR.white, border: `1px solid ${AIR.line2}`, borderRadius: AIR.rPill, boxShadow: AIR.shadowBar },
  seg: { display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2, padding: '10px 24px', borderRadius: AIR.rPill, minWidth: 0, cursor: 'pointer', transition: 'background .15s' },
  segLabel: { fontFamily: AIR.sans, fontSize: 12, fontWeight: 600, color: AIR.ink, lineHeight: 1.2 },
  segInput: { border: 'none', outline: 'none', background: 'transparent', padding: 0, fontFamily: AIR.sans, fontSize: 14, color: AIR.ink, width: '100%', lineHeight: 1.4 },
  divider: { width: 1, alignSelf: 'center', height: 32, background: AIR.line2, flex: 'none' },
  menu: { position: 'absolute', zIndex: 300, top: 'calc(100% + 10px)', left: 0, minWidth: 240, background: AIR.white, borderRadius: 24, boxShadow: AIR.shadowPop, padding: 8, maxHeight: 320, overflowY: 'auto' },
  opt: (on) => ({ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%', padding: '12px 16px', border: 'none', borderRadius: 12, background: on ? AIR.studio : 'transparent', cursor: 'pointer', fontFamily: AIR.sans, fontSize: 15, fontWeight: on ? 600 : 400, color: AIR.ink, textAlign: 'left' }),
  btn: (kind = 'primary') => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 48, padding: '0 24px', borderRadius: AIR.rControl, cursor: 'pointer', whiteSpace: 'nowrap',
    fontFamily: AIR.sans, fontSize: 15, fontWeight: 600,
    border: kind === 'ghost' ? `1px solid ${AIR.ink}` : 'none',
    background: kind === 'primary' ? AIR.ink : kind === 'brand' ? AIR.goldGrad : AIR.white,
    color: kind === 'ghost' ? AIR.ink : AIR.white,
  }),
  roundSearch: { width: 48, height: 48, borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: AIR.goldGrad, color: AIR.white, flex: 'none' },
};

export const AirChevron = ({ open = false, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"
    style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}><path d="M6 9l6 6 6-6" /></svg>
);

const airCheck = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
);

/**
 * Dropdown in the Airbnb manner: a label + value trigger (borderless, so it can
 * sit inside a search pill, or `boxed` on its own) and a rounded floating menu.
 * Optional search box for long lists. Options are strings or { v, l }.
 */
export const AirSelect = ({ label, value, onChange, options, placeholder = 'Any', searchable = false, disabled = false, boxed = false, align = 'left', pad, style = {}, ariaLabel }) => {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const close = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  const opts = options.map(o => (typeof o === 'string' ? { v: o, l: o } : o));
  const shown = q ? opts.filter(o => String(o.l).toLowerCase().includes(q.toLowerCase())) : opts;
  const current = opts.find(o => o.v === value);
  const hasValue = current && current.v !== '' && current.v != null;
  const pick = v => { onChange(v); setOpen(false); setQ(''); };
  const S = AIR_STYLE;
  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 0, ...style }}>
      <button type="button" disabled={disabled} aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel} onClick={() => setOpen(o => !o)}
        className="air-seg"
        style={{
          ...S.seg, width: '100%', textAlign: 'left',
          border: boxed ? `1px solid ${open ? AIR.ink : AIR.field}` : 'none',
          boxShadow: boxed ? (open ? `0 0 0 1px ${AIR.ink}` : 'none') : (open ? '0 6px 20px rgba(0,0,0,0.12)' : 'none'),
          borderRadius: boxed ? AIR.rControl : AIR.rPill,
          background: boxed || open ? AIR.white : 'transparent',
          minHeight: boxed ? 48 : undefined,
          padding: boxed ? (label ? '6px 14px' : '0 14px') : (pad || S.seg.padding),
          opacity: disabled ? 0.45 : 1, cursor: disabled ? 'not-allowed' : 'pointer',
        }}>
        {label && <span style={S.segLabel}>{label}</span>}
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontFamily: AIR.sans, fontSize: boxed ? 15 : 14, color: hasValue ? AIR.ink : AIR.muted, whiteSpace: 'nowrap' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{current ? current.l : placeholder}</span>
          <span style={{ display: 'flex', color: AIR.ink }}><AirChevron open={open} /></span>
        </span>
      </button>
      {open && (
        <div role="listbox" style={{ ...S.menu, [align]: 0 }}>
          {searchable && (
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search"
              style={{ width: '100%', height: 44, margin: '4px 0 8px', padding: '0 16px', border: `1px solid ${AIR.line2}`, borderRadius: 12, fontFamily: AIR.sans, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
          )}
          {shown.length === 0 && <div style={{ padding: '12px 16px', fontFamily: AIR.sans, fontSize: 14, color: AIR.muted }}>No matches</div>}
          {shown.map(o => (
            <button key={String(o.v)} type="button" role="option" aria-selected={o.v === value} onClick={() => pick(o.v)} className="air-opt" style={S.opt(o.v === value)}>
              {o.l}{o.v === value && <span style={{ display: 'flex' }}>{airCheck}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Price range in the Airbnb manner: two separate rounded boxes (Minimum,
 * Maximum) with a ₹ prefix and no up/down arrows. `size="sm"` drops the
 * labels and fits a 40px toolbar. Values are digit strings ('' = no limit).
 */
const digits = (v) => String(v).replace(/[^\d]/g, '');

export const AirMoneyInput = ({ value, onChange, placeholder = 'Any', ariaLabel, size = 'lg', invalid = false, currency = '₹', style = {} }) => (
  <label className={`air-money${size === 'sm' ? ' sm' : ''}${invalid ? ' invalid' : ''}`} style={style}>
    <span className="air-money-cur" aria-hidden="true">{currency}</span>
    <input type="text" inputMode="numeric" autoComplete="off" className="air-bare" aria-label={ariaLabel}
      placeholder={placeholder} value={value ?? ''} onChange={(e) => onChange(digits(e.target.value))} />
  </label>
);

export const AirPriceRange = ({ min, max, onMin, onMax, size = 'lg', labels, minLabel = 'Minimum', maxLabel = 'Maximum', ariaPrefix = '', invalid = false, style = {} }) => {
  const showLabels = labels ?? size !== 'sm';
  const ph = size === 'sm' ? ['Min', 'Max'] : ['Any', 'Any'];
  const box = (label, value, onChange, i) => (
    <div className="air-range-col">
      {showLabels && <span className="air-range-label">{label}</span>}
      <AirMoneyInput size={size} value={value} onChange={onChange} placeholder={ph[i]} invalid={invalid}
        ariaLabel={`${ariaPrefix}${ariaPrefix ? ' ' : ''}${label.toLowerCase()} price`} />
    </div>
  );
  return (
    <div className={`air-range${showLabels ? ' has-labels' : ''}`} style={style}>
      {box(minLabel, min, onMin, 0)}
      <span className="air-range-dash" aria-hidden="true" />
      {box(maxLabel, max, onMax, 1)}
    </div>
  );
};

/**
 * Search-bar segment for price: shows "Price · Any price" and opens a menu
 * with the Minimum / Maximum boxes, as on Airbnb's filters.
 */
const inr = (v) => `₹${Number(v).toLocaleString('en-IN')}`;
export const AirPriceSeg = ({ label = 'Price', min, max, onMin, onMax, pad, align = 'left', style = {} }) => {
  const [open, setOpen] = React.useState(false);
  const [sheet, setSheet] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const close = e => { if (!sheet && ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open, sheet]);
  const toggle = () => { setSheet(window.innerWidth < 640); setOpen(o => !o); };
  const has = min !== '' || max !== '';
  const summary = !has ? 'Any price' : min !== '' && max !== '' ? `${inr(min)} – ${inr(max)}` : min !== '' ? `From ${inr(min)}` : `Up to ${inr(max)}`;
  const S = AIR_STYLE;
  const body = (
    <>
      <div style={{ fontFamily: AIR.sans, fontSize: 16, fontWeight: 600, color: AIR.ink, marginBottom: 4 }}>Price range</div>
      <div style={{ fontFamily: AIR.sans, fontSize: 14, color: AIR.muted, marginBottom: 20 }}>Price per piece</div>
      <AirPriceRange min={min} max={max} onMin={onMin} onMax={onMax} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
        <button type="button" onClick={() => { onMin(''); onMax(''); }} disabled={!has}
          style={{ background: 'none', border: 'none', padding: 0, cursor: has ? 'pointer' : 'default', fontFamily: AIR.sans, fontSize: 14, fontWeight: 600, color: has ? AIR.ink : AIR.faint, textDecoration: 'underline', textUnderlineOffset: 3 }}>Clear</button>
        <button type="button" className="air-btn" onClick={() => setOpen(false)} style={AIR_STYLE.btn('primary')}>Done</button>
      </div>
    </>
  );
  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 0, ...style }}>
      <button type="button" aria-haspopup="dialog" aria-expanded={open} onClick={toggle} className="air-seg"
        style={{ ...S.seg, width: '100%', textAlign: 'left', border: 'none', borderRadius: AIR.rPill, cursor: 'pointer',
          background: open ? AIR.white : 'transparent', boxShadow: open ? '0 6px 20px rgba(0,0,0,0.12)' : 'none', padding: pad || S.seg.padding }}>
        <span style={S.segLabel}>{label}</span>
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontFamily: AIR.sans, fontSize: 14, color: has ? AIR.ink : AIR.muted, whiteSpace: 'nowrap' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{summary}</span>
          <span style={{ display: 'flex', color: AIR.ink }}><AirChevron open={open} /></span>
        </span>
      </button>
      {open && !sheet && (
        <div role="dialog" aria-label={`${label} range`} style={{ ...S.menu, left: align === 'right' ? 'auto' : 0, right: align === 'right' ? 0 : 'auto', width: 340, maxHeight: 'none', overflow: 'visible', padding: 24, boxSizing: 'border-box' }}>
          {body}
        </div>
      )}
      {open && sheet && createPortal(
        <div className="air" style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-end' }}
          onMouseDown={e => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div role="dialog" aria-label={`${label} range`} style={{ width: '100%', background: AIR.white, borderRadius: '24px 24px 0 0', padding: '24px 24px calc(24px + env(safe-area-inset-bottom))', boxSizing: 'border-box', boxShadow: AIR.shadowPop }}>
            {body}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

/** On/off toggle. Renders the switch alone, or with a label row when `label` is given. */
export const AirSwitch = ({ checked, onChange, label, sub, disabled = false, ariaLabel }) => {
  const sw = (
    <button type="button" role="switch" aria-checked={!!checked} aria-label={ariaLabel || (typeof label === 'string' ? label : undefined)}
      disabled={disabled} className="air-switch" onClick={() => onChange(!checked)} />
  );
  if (!label) return sw;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: AIR.sans, fontSize: 15, color: AIR.ink }}>{label}</div>
        {sub && <div style={{ fontFamily: AIR.sans, fontSize: 13, color: AIR.muted, marginTop: 2 }}>{sub}</div>}
      </div>
      {sw}
    </div>
  );
};
